import { absencePolicySchema, sessionBalanceSchema } from './clinicSessions.js';
import { z } from 'zod';
import { CLINIC_THEME_FONTS, clinicThemeBaseSchema, clinicThemeSettingsSchema, } from './clinicTheme.js';
import { businessHoursSchema } from './business.js';
/**
 * Sec Clínica: a clínica (ou o profissional sozinho) como entidade, a equipe com
 * papéis, o catálogo de consultas e tratamentos, os pacientes, a agenda, o
 * financeiro e as avaliações. Tudo pertence à clínica (`clinicId`), nunca à
 * pessoa que digitou. O Secretário descreve e executa; nunca avalia, sugere ou
 * interpreta dado clínico (plano em `docs/plans/clinica/PLANO.md`).
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const isoDateTime = z.iso.datetime();
export const CLINIC_KINDS = [
    'medico',
    'emagrecimento',
    'dentista',
    'nutricao',
    'educacao_fisica',
    'outro',
];
export const clinicKindSchema = z.enum(CLINIC_KINDS);
export const CLINIC_KIND_INFO = {
    medico: { label: 'Consultório médico', hint: 'Consultas, retornos e procedimentos' },
    emagrecimento: {
        label: 'Emagrecimento, hormonal e estética',
        hint: 'Programas por fase com equipe multiprofissional',
    },
    dentista: { label: 'Consultório odontológico', hint: 'Avaliações, limpezas e tratamentos' },
    nutricao: { label: 'Nutrição', hint: 'Consultas e acompanhamento' },
    educacao_fisica: { label: 'Educação física', hint: 'Avaliações, treinos e consultorias' },
    outro: { label: 'Outro', hint: 'Me conta qual é' },
};
// ── Especialidade e serviços (no lugar do "tipo de clínica") ────────────────
/**
 * A especialidade diz o que a clínica é (consultório médico, odontológico…); os serviços
 * dizem o que ela oferece (emagrecimento, hormonal, estética…). Uma clínica médica que faz
 * emagrecimento é as duas coisas, e o catálogo padrão sai da soma. O `kind` antigo continua
 * existindo para os apps já instalados e é derivado daqui.
 */
export const CLINIC_SPECIALTIES = [
    'medico',
    'odontologia',
    'nutricao',
    'educacao_fisica',
    'psicologia',
    'fisioterapia',
    'estetica',
    'outro',
];
export const clinicSpecialtySchema = z.enum(CLINIC_SPECIALTIES);
export const CLINIC_SPECIALTY_INFO = {
    medico: {
        label: 'Consultório médico',
        hint: 'Consultas, retornos e programas',
        registration: 'CRM',
    },
    odontologia: {
        label: 'Consultório odontológico',
        hint: 'Avaliações, limpezas e tratamentos',
        registration: 'CRO',
    },
    nutricao: { label: 'Nutrição', hint: 'Consultas e acompanhamento', registration: 'CRN' },
    educacao_fisica: {
        label: 'Educação física',
        hint: 'Avaliações, treinos e consultorias',
        registration: 'CREF',
    },
    psicologia: { label: 'Psicologia', hint: 'Sessões e acompanhamento', registration: 'CRP' },
    fisioterapia: { label: 'Fisioterapia', hint: 'Avaliações e sessões', registration: 'CREFITO' },
    estetica: { label: 'Estética', hint: 'Procedimentos e protocolos', registration: 'Registro' },
    outro: { label: 'Outro', hint: 'Me conta qual é', registration: 'Registro' },
};
export const CLINIC_SERVICES = [
    'emagrecimento',
    'hormonal',
    'estetica',
    'saude_sexual',
    'check_up',
    'nutricao',
    'treino',
    'ortodontia',
    'implantes',
    'clareamento',
    'psicoterapia',
    'reabilitacao',
];
export const clinicServiceSchema = z.enum(CLINIC_SERVICES);
export const CLINIC_SERVICE_INFO = {
    emagrecimento: {
        label: 'Emagrecimento',
        seeds: [
            { kind: 'consulta', name: 'Bioimpedância', durationMin: 20 },
            {
                kind: 'tratamento',
                name: 'Emagrecimento',
                months: 6,
                reviewEveryDays: 30,
                phases: ['Início', 'Acompanhamento', 'Manutenção'],
            },
            { kind: 'complemento', name: 'Bioimpedância avulsa' },
        ],
    },
    hormonal: {
        label: 'Hormonal',
        seeds: [
            {
                kind: 'tratamento',
                name: 'Reposição hormonal',
                months: 12,
                reviewEveryDays: 45,
                phases: ['Ajuste', 'Manutenção'],
            },
        ],
    },
    estetica: {
        label: 'Estética',
        seeds: [
            { kind: 'consulta', name: 'Avaliação estética', durationMin: 40 },
            {
                kind: 'tratamento',
                name: 'Protocolo estético',
                months: 3,
                reviewEveryDays: 15,
                phases: ['Sessões', 'Manutenção'],
            },
        ],
    },
    saude_sexual: {
        label: 'Saúde sexual',
        seeds: [
            {
                kind: 'tratamento',
                name: 'Saúde sexual',
                months: 6,
                reviewEveryDays: 30,
                phases: ['Início', 'Acompanhamento', 'Manutenção'],
            },
        ],
    },
    check_up: {
        label: 'Check-up',
        seeds: [{ kind: 'consulta', name: 'Check-up', durationMin: 60 }],
    },
    nutricao: {
        label: 'Nutrição',
        seeds: [
            { kind: 'consulta', name: 'Avaliação nutricional', durationMin: 40 },
            { kind: 'complemento', name: 'Nutricionista', months: 1 },
        ],
    },
    treino: {
        label: 'Treino',
        seeds: [
            { kind: 'consulta', name: 'Treino', durationMin: 60 },
            { kind: 'complemento', name: 'Educador físico', months: 1 },
        ],
    },
    ortodontia: {
        label: 'Ortodontia',
        seeds: [
            {
                kind: 'tratamento',
                name: 'Aparelho',
                months: 24,
                reviewEveryDays: 30,
                phases: ['Instalação', 'Ajustes', 'Contenção'],
            },
        ],
    },
    implantes: {
        label: 'Implantes',
        seeds: [
            {
                kind: 'tratamento',
                name: 'Implante',
                months: 6,
                reviewEveryDays: 60,
                phases: ['Cirurgia', 'Cicatrização', 'Prótese'],
            },
        ],
    },
    clareamento: {
        label: 'Clareamento',
        seeds: [
            {
                kind: 'tratamento',
                name: 'Clareamento',
                months: 1,
                reviewEveryDays: 15,
                phases: ['Sessões'],
            },
        ],
    },
    psicoterapia: {
        label: 'Psicoterapia',
        seeds: [
            { kind: 'consulta', name: 'Sessão', durationMin: 50 },
            {
                kind: 'tratamento',
                name: 'Acompanhamento psicológico',
                months: 6,
                reviewEveryDays: 7,
                phases: ['Acolhimento', 'Processo', 'Alta'],
            },
        ],
    },
    reabilitacao: {
        label: 'Reabilitação',
        seeds: [
            { kind: 'consulta', name: 'Sessão de fisioterapia', durationMin: 50 },
            {
                kind: 'tratamento',
                name: 'Reabilitação',
                months: 3,
                reviewEveryDays: 7,
                phases: ['Avaliação', 'Sessões', 'Alta'],
            },
        ],
    },
};
/** Serviços que fazem sentido para cada especialidade (a clínica marca os que oferece). */
export const CLINIC_SPECIALTY_SERVICES = {
    medico: [
        'emagrecimento',
        'hormonal',
        'estetica',
        'saude_sexual',
        'check_up',
        'nutricao',
        'treino',
    ],
    odontologia: ['ortodontia', 'implantes', 'clareamento', 'estetica'],
    nutricao: ['emagrecimento', 'nutricao', 'treino'],
    // Com nutricionista na equipe (P3), o estúdio também atende nutrição.
    educacao_fisica: ['treino', 'emagrecimento', 'nutricao'],
    psicologia: ['psicoterapia'],
    fisioterapia: ['reabilitacao', 'treino'],
    estetica: ['estetica', 'emagrecimento'],
    outro: CLINIC_SERVICES,
};
/** O que toda clínica da especialidade atende, antes dos serviços. */
const SPECIALTY_BASE = {
    medico: [
        { kind: 'consulta', name: 'Primeira consulta', durationMin: 50 },
        { kind: 'consulta', name: 'Retorno', durationMin: 30 },
        { kind: 'consulta', name: 'Procedimento', durationMin: 60 },
        { kind: 'complemento', name: 'Exames de apoio' },
    ],
    odontologia: [
        { kind: 'consulta', name: 'Avaliação', durationMin: 30 },
        { kind: 'consulta', name: 'Limpeza', durationMin: 45 },
        { kind: 'consulta', name: 'Retorno', durationMin: 20 },
        { kind: 'consulta', name: 'Urgência', durationMin: 30 },
        { kind: 'complemento', name: 'Restauração' },
        { kind: 'complemento', name: 'Extração' },
    ],
    nutricao: [
        { kind: 'consulta', name: 'Primeira consulta', durationMin: 60 },
        { kind: 'consulta', name: 'Retorno', durationMin: 30 },
        {
            kind: 'tratamento',
            name: 'Acompanhamento nutricional',
            months: 3,
            reviewEveryDays: 30,
            phases: ['Início', 'Ajustes', 'Manutenção'],
        },
    ],
    // O que o personal vende (estudo do personal): avaliação, sessão avulsa, mensalidade
    // presencial e online, e pacote de sessões.
    educacao_fisica: [
        { kind: 'consulta', name: 'Avaliação física', durationMin: 60 },
        { kind: 'consulta', name: 'Sessão de treino', durationMin: 60 },
        {
            kind: 'tratamento',
            name: 'Personal presencial (mensal)',
            months: 1,
            reviewEveryDays: 30,
            renews: true,
        },
        {
            kind: 'tratamento',
            name: 'Consultoria online (mensal)',
            months: 1,
            reviewEveryDays: 30,
            renews: true,
        },
        { kind: 'tratamento', name: 'Pacote de 10 sessões', months: 2, sessions: 10 },
    ],
    psicologia: [{ kind: 'consulta', name: 'Primeira sessão', durationMin: 50 }],
    fisioterapia: [{ kind: 'consulta', name: 'Avaliação', durationMin: 50 }],
    estetica: [{ kind: 'consulta', name: 'Avaliação', durationMin: 40 }],
    outro: [
        { kind: 'consulta', name: 'Atendimento', durationMin: 60 },
        { kind: 'consulta', name: 'Retorno', durationMin: 30 },
        {
            kind: 'tratamento',
            name: 'Tratamento 1',
            months: 3,
            reviewEveryDays: 30,
            phases: ['Início', 'Acompanhamento'],
        },
    ],
};
/** Catálogo padrão: a base da especialidade mais o de cada serviço, sem repetir nome. */
export function catalogSeedsFor(specialty, services) {
    const out = [];
    for (const seed of [
        ...SPECIALTY_BASE[specialty],
        ...services.flatMap((s) => CLINIC_SERVICE_INFO[s].seeds),
    ])
        if (!out.some((item) => item.kind === seed.kind && item.name === seed.name))
            out.push(seed);
    return out;
}
/** O `kind` antigo, para os apps já instalados (e para a regra de preço na página). */
export function kindOfSpecialty(specialty, services) {
    if (specialty === 'odontologia')
        return 'dentista';
    if (specialty === 'nutricao')
        return 'nutricao';
    if (specialty === 'educacao_fisica')
        return 'educacao_fisica';
    if (specialty === 'medico')
        return services.some((s) => s === 'emagrecimento' || s === 'hormonal' || s === 'estetica')
            ? 'emagrecimento'
            : 'medico';
    return 'outro';
}
/** Do `kind` antigo para especialidade e serviços (apps antigos criando clínica). */
export function specialtyOfKind(kind) {
    switch (kind) {
        case 'emagrecimento':
            return { specialty: 'medico', services: ['emagrecimento', 'hormonal', 'estetica'] };
        case 'dentista':
            return { specialty: 'odontologia', services: [] };
        case 'nutricao':
            return { specialty: 'nutricao', services: [] };
        case 'educacao_fisica':
            return { specialty: 'educacao_fisica', services: [] };
        case 'medico':
            return { specialty: 'medico', services: [] };
        default:
            return { specialty: 'outro', services: [] };
    }
}
/** "Consultório médico · Emagrecimento, hormonal e estética". */
export function clinicTitle(specialty, services) {
    const base = CLINIC_SPECIALTY_INFO[specialty].label;
    if (services.length === 0)
        return base;
    const names = services.map((s, i) => i === 0 ? CLINIC_SERVICE_INFO[s].label : CLINIC_SERVICE_INFO[s].label.toLowerCase());
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} e ${names.at(-1)}`;
    return `${base} · ${list}`;
}
export const CLINIC_SERVICES_MAX = 12;
export const CLINIC_ROLES = ['dono', 'profissional', 'recepcao'];
export const clinicRoleSchema = z.enum(CLINIC_ROLES);
export const CLINIC_ROLE_LABEL = {
    dono: 'Dono',
    profissional: 'Profissional',
    recepcao: 'Recepção',
};
export const clinicPermissionsSchema = z.object({
    /** Vê e lança pagamentos, vê receita e atraso. */
    financeiro: z.boolean(),
    /** Vê avaliações, resultados e anotações clínicas. */
    clinico: z.boolean(),
    /**
     * Gestão (rodada 15): equipe, convites, configurações, catálogo e apagar paciente, como
     * o dono (menos o plano). Opcional para os apps antigos; o dono sempre tem.
     */
    gestao: z.boolean().optional(),
    /** Envios em lote (T7.41): o dono e a Gestão sempre; os outros quando o dono libera. */
    envios: z.boolean().optional(),
});
export const CLINIC_NAME_MAX = 80;
export const CLINIC_SLUG_MAX = 40;
export const CLINIC_DESCRIPTION_MAX = 600;
export const CLINIC_NOTES_MAX = 2000;
export const CLINIC_CATALOG_MAX = 80;
export const CLINIC_PHASES_MAX = 8;
export const CLINIC_LIST_PAGE = 50;
export const CLINIC_SEARCH_MIN = 2;
const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/, 'Só letras, números e hífen');
/** Caminhos de secretarios.net que uma clínica nunca pode usar como endereço. */
export const CLINIC_RESERVED_SLUGS = new Set([
    'login',
    'api',
    'assistente-pessoal',
    'capa',
    'teste',
    'tela',
    'funcoes',
    'ajuda',
    'c',
    'app',
    'dono',
    'privacidade',
    'termos',
    'suporte',
    'precos',
    'planos',
    'sobre',
    'contato',
    'blog',
    'admin',
    'static',
    'assets',
]);
/** O que a página pública mostra e permite (Mais › Página pública, estudo E12). */
export const clinicPublicSchema = z.object({
    hours: z.array(businessHoursSchema).max(14),
    address: z.string().trim().max(120).nullable(),
    /** Mostrar preços na página (quando a profissão permite). */
    showPrices: z.boolean(),
    /** Botão Agendar: horários livres da agenda da clínica. */
    allowBooking: z.boolean(),
    /** Botão Acompanhar resultados (abre a aba Saúde). */
    allowFollow: z.boolean(),
    /** Página no ar. Desligar tira a página sem apagar nada. */
    live: z.boolean(),
});
export const DEFAULT_CLINIC_PUBLIC = {
    hours: [1, 2, 3, 4, 5].map((day) => ({ day, open: '08:00', close: '18:00' })),
    address: null,
    showPrices: true,
    allowBooking: true,
    allowFollow: true,
    live: true,
};
/** Profissões que não divulgam preço em página pública (códigos de ética). */
export const CLINIC_NO_PUBLIC_PRICE = new Set(['medico', 'dentista', 'nutricao']);
/** Ajustes da clínica que valem para a mesa (migração 0062). */
export const CLINIC_WEEKLY_GOAL_MAX = 500;
/** Regime de impostos (estudo do financeiro, etapa 5); "indefinido" = ainda sem estimativa. */
export const CLINIC_TAX_REGIMES = ['simples', 'presumido', 'indefinido'];
export const clinicTaxRegimeSchema = z.enum(CLINIC_TAX_REGIMES);
export const clinicFinanceSettingsSchema = z.object({
    regime: clinicTaxRegimeSchema,
    /** CNAE principal, como 8630-5/03. */
    cnae: z
        .string()
        .regex(/^\d{4}-\d\/\d{2}$/, 'CNAE no formato 0000-0/00')
        .nullable(),
    /** ISS do município, em centésimos de por cento (200 = 2%); só pesa no presumido. */
    issBp: z.number().int().min(0).max(500),
    /** Equiparação hospitalar no presumido (base de 8% e 12%); tem requisitos. */
    hospitalar: z.boolean(),
    /** Lucro que o dono quer no mês, somado ao ponto de equilíbrio para dar a meta. */
    profitGoalCents: z.number().int().min(0).max(10_000_000_000),
});
export const DEFAULT_CLINIC_FINANCE_SETTINGS = {
    regime: 'indefinido',
    cnae: null,
    issBp: 200,
    hospitalar: false,
    profitGoalCents: 0,
};
export const clinicSettingsSchema = z.object({
    /** Consultas a marcar por semana: o "Leads para agendar" da Início. */
    weeklyGoal: z.number().int().min(1).max(CLINIC_WEEKLY_GOAL_MAX),
    /** Pedidos de medicamento ligados (página Medicamentos e o número da Início). */
    medicationOrders: z.boolean(),
    /** Taxas da maquininha (estudo do financeiro); sem elas, as do padrão. */
    cardFees: z
        .object({
        /** Débito, em centésimos de por cento (86 = 0,86%). */
        debitBp: z.number().int().min(0).max(2000),
        /** Crédito de 1x a 12x (posição 0 = à vista). */
        creditBp: z.array(z.number().int().min(0).max(3000)).length(12),
    })
        .optional(),
    /** Impostos e meta do mês; só quem vê o financeiro recebe. */
    finance: clinicFinanceSettingsSchema.optional(),
    /** Tema e letra da clínica (T7.25); sem ele, o do Sec. */
    theme: clinicThemeSettingsSchema.optional(),
    /** Telemedicina (rodada 15): a sala fixa da clínica (Google Meet, Zoom, Whereby…). */
    telemedicine: z.object({ defaultUrl: z.url().max(300).nullable() }).optional(),
    /** Faltas e reposição do estúdio (P2 do personal); sem ela, a regra padrão. */
    absence: absencePolicySchema.optional(),
});
/** Ponto de partida das taxas: as que o APOLO usa (débito 0,86%; crédito 2,98% à vista e 4,54% a 11,95% de 2x a 12x). */
export const DEFAULT_CARD_FEES = {
    debitBp: 86,
    creditBp: [298, 454, 528, 602, 676, 750, 824, 898, 972, 1047, 1121, 1195],
};
export const DEFAULT_CLINIC_SETTINGS = { weeklyGoal: 25, medicationOrders: false };
export const clinicSchema = z.object({
    id: z.uuid(),
    /**
     * O plano do dono inclui a clínica (leva 1). Falso: ler continua, mexer pede renovar.
     * Opcional para os apps antigos.
     */
    active: z.boolean().optional(),
    kind: clinicKindSchema,
    /** O que a clínica é e o que oferece (o `kind` é derivado, para os apps antigos). */
    specialty: clinicSpecialtySchema,
    services: z.array(clinicServiceSchema),
    /** "Consultório médico · Emagrecimento, hormonal e estética". */
    title: z.string(),
    name: z.string().trim().min(1).max(CLINIC_NAME_MAX),
    slug: slugSchema.nullable(),
    description: z.string().max(CLINIC_DESCRIPTION_MAX).nullable(),
    /** Registro do responsável (CRM, CRO, CRN, CREF), como a clínica escreve. */
    registration: z.string().max(40).nullable(),
    /** Minha ligação com a clínica. */
    role: clinicRoleSchema,
    permissions: clinicPermissionsSchema,
    /** Minha profissão na equipe (P3): decide o que eu edito; sem ela, tudo como antes. */
    profession: clinicSpecialtySchema.nullable().optional(),
    membersCount: z.number().int().min(0),
    patientsCount: z.number().int().min(0),
    public: clinicPublicSchema,
    settings: clinicSettingsSchema,
    createdAt: isoDateTime,
});
export const createClinicSchema = z
    .object({
    /** Apps antigos mandam só o tipo; os novos, especialidade e serviços. */
    kind: clinicKindSchema.optional(),
    specialty: clinicSpecialtySchema.optional(),
    services: z.array(clinicServiceSchema).max(CLINIC_SERVICES_MAX).optional(),
    name: z.string().trim().min(1).max(CLINIC_NAME_MAX),
    registration: z.string().trim().max(40).optional(),
})
    .refine((value) => value.kind !== undefined || value.specialty !== undefined, {
    message: 'Escolha a especialidade.',
});
export const updateClinicSchema = z
    .object({
    name: z.string().trim().min(1).max(CLINIC_NAME_MAX),
    slug: slugSchema.nullable(),
    description: z.string().trim().max(CLINIC_DESCRIPTION_MAX).nullable(),
    registration: z.string().trim().max(40).nullable(),
    public: clinicPublicSchema.partial(),
    specialty: clinicSpecialtySchema,
    services: z.array(clinicServiceSchema).max(CLINIC_SERVICES_MAX),
    settings: clinicSettingsSchema.partial(),
})
    .partial();
export const clinicMemberSchema = z.object({
    userId: z.uuid(),
    name: z.string(),
    username: z.string().nullable(),
    role: clinicRoleSchema,
    /** Como aparece na agenda ("Dr. Igor", "Dra. Paula"). */
    title: z.string().max(60).nullable(),
    specialty: z.string().max(60).nullable(),
    permissions: clinicPermissionsSchema,
    joinedAt: isoDateTime,
    /** Atendimento (rodada 15): null = igual ao funcionamento da clínica; [] = não atende. */
    hours: z.array(businessHoursSchema).max(14).nullable().optional(),
    /** Profissão na equipe (P3 do personal): a nutricionista não edita treino; o personal, dieta. */
    profession: clinicSpecialtySchema.nullable().optional(),
});
export const updateMemberSchema = z
    .object({
    role: z.enum(['profissional', 'recepcao']),
    title: z.string().trim().max(60).nullable(),
    specialty: z.string().trim().max(60).nullable(),
    permissions: clinicPermissionsSchema,
    hours: z.array(businessHoursSchema).max(14).nullable(),
    profession: clinicSpecialtySchema.nullable(),
})
    .partial();
export const clinicInviteSchema = z.object({
    /** Código que entra no link de convite; vale 7 dias. */
    code: z.string(),
    role: z.enum(['profissional', 'recepcao']),
    expiresAt: isoDateTime,
});
// ── Catálogo ────────────────────────────────────────────────────────────────
export const CATALOG_KINDS = ['consulta', 'tratamento', 'complemento', 'outro'];
export const catalogKindSchema = z.enum(CATALOG_KINDS);
export const CATALOG_KIND_LABEL = {
    consulta: 'Consultas',
    tratamento: 'Tratamentos',
    complemento: 'Complementos',
    outro: 'Outros',
};
const phaseSchema = z.string().trim().min(1).max(40);
/**
 * Programa conjunto (P3 do personal): um tratamento que inclui consultas de vários
 * profissionais (nutricionista, personal, bioimpedância), com um preço e uma cobrança só.
 * Cada parte é um item de consulta do catálogo e quantas vezes entra no programa.
 */
export const PROGRAM_PARTS_MAX = 6;
export const programPartSchema = z.object({
    catalogId: z.uuid(),
    name: z.string().trim().min(1).max(80),
    quantity: z.number().int().min(1).max(200),
});
/** O andamento do programa: por parte, quantas foram feitas e quantas estão marcadas. */
export const programProgressSchema = z.object({
    treatmentId: z.uuid(),
    name: z.string(),
    parts: z.array(z.object({
        name: z.string(),
        quantity: z.number().int(),
        done: z.number().int(),
        scheduled: z.number().int(),
    })),
});
/**
 * A conta do programa: consultas do item de cada parte desde o começo do tratamento; feitas
 * são as com presença, marcadas são as que ainda vão acontecer.
 */
export function programProgress(treatment, appointments, now) {
    const parts = treatment.parts ?? [];
    if (parts.length === 0)
        return null;
    const since = appointments.filter((row) => row.startAt.slice(0, 10) >= treatment.startDay);
    return {
        treatmentId: treatment.id,
        name: treatment.name,
        parts: parts.map((part) => {
            const mine = since.filter((row) => row.catalogId === part.catalogId);
            return {
                name: part.name,
                quantity: part.quantity,
                done: mine.filter((row) => row.status === 'compareceu').length,
                scheduled: mine.filter((row) => (row.status === 'marcado' || row.status === 'confirmado') &&
                    new Date(row.startAt).getTime() >= now.getTime()).length,
            };
        }),
    };
}
export const catalogItemSchema = z.object({
    id: z.uuid(),
    kind: catalogKindSchema,
    name: z.string().trim().min(1).max(80),
    priceCents: z.number().int().min(0).max(100_000_000).nullable(),
    /** Consultas: duração do horário na agenda. */
    durationMin: z.number().int().min(5).max(480).nullable(),
    /** Tratamentos e complementos: duração em meses. */
    months: z.number().int().min(1).max(60).nullable(),
    reviewEveryDays: z.number().int().min(1).max(365).nullable(),
    phases: z.array(phaseSchema).max(CLINIC_PHASES_MAX),
    /** Ainda com o nome do padrão (aparece apagado até a clínica renomear). */
    isDefault: z.boolean(),
    active: z.boolean(),
    sort: z.number().int(),
    /** O que a página pública diz a quem marca este item ("traga os exames"). */
    bookingNote: z.string().trim().max(160).nullable(),
    /** Tratamentos: medicamentos que o pedido leva; com itens, iniciar abre o pedido sozinho. */
    medicationItems: z
        .array(z.object({ name: z.string().trim().min(1).max(80), quantity: z.string().trim().max(40) }))
        .max(10),
    /** Pacote: quantas sessões o aluno compra (o uso conta as com presença marcada). */
    sessions: z.number().int().min(1).max(200).nullable().optional(),
    /** Mensalidade: renova sozinho no fim do período e lança a cobrança seguinte. */
    renews: z.boolean().optional(),
    /** Tratamento: programa conjunto (consultas de vários profissionais numa cobrança só). */
    parts: z.array(programPartSchema).max(PROGRAM_PARTS_MAX).optional(),
});
export const createCatalogItemSchema = catalogItemSchema
    .omit({ id: true, isDefault: true, sort: true })
    .partial({
    priceCents: true,
    durationMin: true,
    months: true,
    reviewEveryDays: true,
    phases: true,
    active: true,
    bookingNote: true,
    medicationItems: true,
    sessions: true,
    renews: true,
    parts: true,
});
export const updateCatalogItemSchema = catalogItemSchema
    .omit({ id: true, isDefault: true, kind: true })
    .partial();
/**
 * Padrão por tipo de negócio (estudo E1, seção 9): abrangente, sem personalizar
 * demais. Nomes genéricos ("Tratamento 1") ficam marcados como padrão até a
 * clínica renomear. Preço sempre vazio: é da clínica.
 */
export const CLINIC_CATALOG_DEFAULTS = {
    medico: [
        { kind: 'consulta', name: 'Primeira consulta', durationMin: 50 },
        { kind: 'consulta', name: 'Retorno', durationMin: 30 },
        { kind: 'consulta', name: 'Avaliação', durationMin: 60 },
        { kind: 'consulta', name: 'Procedimento', durationMin: 60 },
        {
            kind: 'tratamento',
            name: 'Tratamento 1',
            months: 6,
            reviewEveryDays: 30,
            phases: ['Início', 'Acompanhamento', 'Manutenção'],
        },
        {
            kind: 'tratamento',
            name: 'Tratamento 2',
            months: 6,
            reviewEveryDays: 30,
            phases: ['Início', 'Acompanhamento', 'Manutenção'],
        },
        { kind: 'complemento', name: 'Exames de apoio' },
    ],
    emagrecimento: [
        { kind: 'consulta', name: 'Primeira consulta', durationMin: 60 },
        { kind: 'consulta', name: 'Retorno', durationMin: 30 },
        { kind: 'consulta', name: 'Bioimpedância', durationMin: 20 },
        { kind: 'consulta', name: 'Avaliação nutricional', durationMin: 40 },
        { kind: 'consulta', name: 'Treino', durationMin: 60 },
        {
            kind: 'tratamento',
            name: 'Emagrecimento',
            months: 6,
            reviewEveryDays: 30,
            phases: ['Início', 'Acompanhamento', 'Manutenção'],
        },
        {
            kind: 'tratamento',
            name: 'Reposição hormonal',
            months: 12,
            reviewEveryDays: 45,
            phases: ['Ajuste', 'Manutenção'],
        },
        {
            kind: 'tratamento',
            name: 'Programa 12 meses',
            months: 12,
            reviewEveryDays: 30,
            phases: ['Mês 1 a 3', 'Mês 4 a 6', 'Mês 7 a 9', 'Mês 10 a 12'],
        },
        { kind: 'complemento', name: 'Nutricionista', months: 1 },
        { kind: 'complemento', name: 'Educador físico', months: 1 },
        { kind: 'complemento', name: 'Bioimpedância avulsa' },
    ],
    dentista: [
        { kind: 'consulta', name: 'Avaliação', durationMin: 30 },
        { kind: 'consulta', name: 'Limpeza', durationMin: 45 },
        { kind: 'consulta', name: 'Retorno', durationMin: 20 },
        { kind: 'consulta', name: 'Urgência', durationMin: 30 },
        {
            kind: 'tratamento',
            name: 'Aparelho',
            months: 24,
            reviewEveryDays: 30,
            phases: ['Instalação', 'Ajustes', 'Contenção'],
        },
        {
            kind: 'tratamento',
            name: 'Clareamento',
            months: 1,
            reviewEveryDays: 15,
            phases: ['Sessões'],
        },
        {
            kind: 'tratamento',
            name: 'Implante',
            months: 6,
            reviewEveryDays: 60,
            phases: ['Cirurgia', 'Cicatrização', 'Prótese'],
        },
        { kind: 'tratamento', name: 'Canal', months: 1, reviewEveryDays: 7, phases: ['Sessões'] },
        { kind: 'complemento', name: 'Restauração' },
        { kind: 'complemento', name: 'Extração' },
    ],
    nutricao: [
        { kind: 'consulta', name: 'Primeira consulta', durationMin: 60 },
        { kind: 'consulta', name: 'Retorno', durationMin: 30 },
        { kind: 'consulta', name: 'Bioimpedância', durationMin: 20 },
        {
            kind: 'tratamento',
            name: 'Acompanhamento 3 meses',
            months: 3,
            reviewEveryDays: 30,
            phases: ['Acompanhamento'],
        },
        {
            kind: 'tratamento',
            name: 'Acompanhamento 6 meses',
            months: 6,
            reviewEveryDays: 30,
            phases: ['Acompanhamento'],
        },
        { kind: 'complemento', name: 'Plano alimentar avulso' },
    ],
    educacao_fisica: [
        { kind: 'consulta', name: 'Avaliação física', durationMin: 45 },
        { kind: 'consulta', name: 'Treino presencial', durationMin: 60 },
        { kind: 'consulta', name: 'Treino online', durationMin: 30 },
        {
            kind: 'tratamento',
            name: 'Mensal presencial',
            months: 1,
            reviewEveryDays: 7,
            phases: ['Treinos'],
        },
        {
            kind: 'tratamento',
            name: 'Pacote 10 sessões',
            months: 2,
            reviewEveryDays: 7,
            phases: ['Sessões'],
        },
        {
            kind: 'tratamento',
            name: 'Consultoria online',
            months: 3,
            reviewEveryDays: 15,
            phases: ['Acompanhamento'],
        },
        { kind: 'complemento', name: 'Reavaliação' },
    ],
    outro: [
        { kind: 'consulta', name: 'Consulta', durationMin: 50 },
        { kind: 'consulta', name: 'Retorno', durationMin: 30 },
        {
            kind: 'tratamento',
            name: 'Tratamento 1',
            months: 3,
            reviewEveryDays: 30,
            phases: ['Acompanhamento'],
        },
    ],
};
/** Nomes que contam como "ainda padrão" (aparecem apagados até a clínica renomear). */
export const isDefaultCatalogName = (name) => /^tratamento \d+$/i.test(name.trim());
// ── Pacientes ───────────────────────────────────────────────────────────────
export const PATIENT_STATUSES = [
    'lead',
    'agendado',
    'ativo',
    'finalizado',
    'cancelado',
    'inativo',
];
export const patientStatusSchema = z.enum(PATIENT_STATUSES);
export const PATIENT_STATUS_LABEL = {
    lead: 'Chegou agora',
    agendado: 'Agendado',
    ativo: 'Ativo',
    finalizado: 'Finalizado',
    cancelado: 'Cancelado',
    inativo: 'Inativo',
};
export const patientSexSchema = z.enum(['f', 'm']);
export const patientSchema = z.object({
    id: z.uuid(),
    name: z.string().trim().min(1).max(80),
    /** Como a pessoa escreve ("(27) 9 9999-0001"); o número cru fica cifrado. */
    phone: z.string().max(32).nullable(),
    status: patientStatusSchema,
    birth: isoDate.nullable(),
    sex: patientSexSchema.nullable(),
    email: z.string().max(120).nullable(),
    cpf: z.string().max(14).nullable(),
    origin: z.string().max(60).nullable(),
    notes: z.string().max(CLINIC_NOTES_MAX),
    /** Usuário do Sec ligado (depois do consentimento dele). */
    secUserId: z.uuid().nullable(),
    /** Resumo do tratamento atual, para a lista. */
    treatment: z
        .object({
        id: z.uuid(),
        name: z.string(),
        phase: z.number().int().min(0),
        phases: z.array(z.string()),
        monthOf: z.number().int().min(1).nullable(),
        months: z.number().int().min(1).nullable(),
        nextReviewDay: isoDate.nullable(),
        financialStatus: z.enum(['ok', 'atencao']),
    })
        .nullable(),
    /** Pagamento vencido e não pago. */
    overdueCents: z.number().int().min(0),
    nextAppointmentAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
});
export const createPatientSchema = z.object({
    name: z.string().trim().min(1).max(80),
    phone: z.string().trim().max(32).optional(),
    status: patientStatusSchema.optional(),
    birth: isoDate.optional(),
    sex: patientSexSchema.optional(),
    email: z.string().trim().max(120).optional(),
    cpf: z.string().trim().max(14).optional(),
    origin: z.string().trim().max(60).optional(),
    notes: z.string().trim().max(CLINIC_NOTES_MAX).optional(),
    /** Marketing (rodada 15): campanha e quem indicou (outro paciente). */
    campaign: z.string().trim().max(80).optional(),
    referredById: z.uuid().optional(),
});
export const updatePatientSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    phone: z.string().trim().max(32).nullable(),
    status: patientStatusSchema,
    birth: isoDate.nullable(),
    sex: patientSexSchema.nullable(),
    email: z.string().trim().max(120).nullable(),
    cpf: z.string().trim().max(14).nullable(),
    origin: z.string().trim().max(60).nullable(),
    notes: z.string().trim().max(CLINIC_NOTES_MAX),
    campaign: z.string().trim().max(80).nullable(),
    referredById: z.uuid().nullable(),
})
    .partial();
/** De onde a pessoa veio (rodada 15): a primeira origem não muda quando a da ficha muda. */
export const patientMarketingSchema = z.object({
    firstOrigin: z.string().nullable(),
    campaign: z.string().nullable(),
    referredBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
export const patientListQuerySchema = z.object({
    q: z.string().trim().max(80).optional(),
    status: z.enum(['ativos', 'leads', 'atraso', 'finalizados', 'todos']).optional(),
    cursor: z.string().max(200).optional(),
});
export const patientListSchema = z.object({
    patients: z.array(patientSchema),
    nextCursor: z.string().nullable(),
    counts: z.object({
        ativos: z.number().int().min(0),
        leads: z.number().int().min(0),
        atraso: z.number().int().min(0),
        finalizados: z.number().int().min(0),
    }),
});
// ── Tratamentos ─────────────────────────────────────────────────────────────
export const TREATMENT_STATUSES = ['ativo', 'finalizado', 'cancelado'];
export const treatmentStatusSchema = z.enum(TREATMENT_STATUSES);
export const PAYMENT_METHODS = [
    'pix',
    'dinheiro',
    'debito',
    'credito',
    'boleto',
    'transferencia',
    'outro',
];
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export const PAYMENT_METHOD_LABEL = {
    pix: 'Pix',
    dinheiro: 'Dinheiro',
    debito: 'Débito',
    credito: 'Crédito',
    boleto: 'Boleto',
    transferencia: 'Transferência',
    outro: 'Outro',
};
/** Uma parte do pagamento (estudo do financeiro): forma, valor, vezes e o primeiro vencimento. */
export const paymentPartSchema = z.object({
    method: paymentMethodSchema,
    cents: z.number().int().min(1).max(100_000_000),
    times: z.number().int().min(1).max(24),
    firstDueDay: isoDate.nullable(),
});
export const paymentPlanSchema = z.object({
    entryCents: z.number().int().min(0).max(100_000_000),
    installments: z.number().int().min(0).max(60),
    installmentCents: z.number().int().min(0).max(100_000_000),
    method: paymentMethodSchema.nullable(),
    /** Dia do primeiro vencimento das parcelas. */
    firstDueDay: isoDate.nullable(),
    /** Preço de tabela e desconto (o valor fechado é o `priceCents` do tratamento). */
    listPriceCents: z.number().int().min(0).max(100_000_000).nullable().optional(),
    discountCents: z.number().int().min(0).max(100_000_000).optional(),
    discountReason: z.string().trim().max(60).optional(),
    /** Forma da entrada e se ela já foi paga na hora. */
    entryMethod: paymentMethodSchema.nullable().optional(),
    entryPaid: z.boolean().optional(),
    /** O resto, em partes (Pix parcelado, crédito na maquininha, boleto…). */
    parts: z.array(paymentPartSchema).max(4).optional(),
});
export const treatmentSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    catalogId: z.uuid().nullable(),
    name: z.string(),
    professionalId: z.uuid().nullable(),
    status: treatmentStatusSchema,
    phase: z.number().int().min(0),
    phases: z.array(z.string()),
    startDay: isoDate,
    plannedEndDay: isoDate.nullable(),
    endDay: isoDate.nullable(),
    months: z.number().int().min(1).nullable(),
    reviewEveryDays: z.number().int().min(1).nullable(),
    nextReviewDay: isoDate.nullable(),
    priceCents: z.number().int().min(0).nullable(),
    paymentPlan: paymentPlanSchema.nullable(),
    financialStatus: z.enum(['ok', 'atencao']),
    notes: z.string().max(CLINIC_NOTES_MAX),
    createdAt: isoDateTime,
    /** Pacote de sessões: quantas foram compradas. */
    sessions: z.number().int().min(1).nullable().optional(),
    /** Mensalidade que renova sozinha. */
    renews: z.boolean().optional(),
    /** Programa conjunto: as partes, congeladas quando o tratamento começou. */
    parts: z.array(programPartSchema).max(PROGRAM_PARTS_MAX).optional(),
});
export const startTreatmentSchema = z.object({
    catalogId: z.uuid().optional(),
    /** Quando não vem do catálogo. */
    name: z.string().trim().min(1).max(80).optional(),
    professionalId: z.uuid().optional(),
    startDay: isoDate.optional(),
    months: z.number().int().min(1).max(60).optional(),
    reviewEveryDays: z.number().int().min(1).max(365).optional(),
    phases: z.array(phaseSchema).max(CLINIC_PHASES_MAX).optional(),
    priceCents: z.number().int().min(0).max(100_000_000).optional(),
    paymentPlan: paymentPlanSchema.optional(),
    sessions: z.number().int().min(1).max(200).optional(),
    renews: z.boolean().optional(),
    notes: z.string().trim().max(CLINIC_NOTES_MAX).optional(),
    /** Medicamentos escolhidos na folha; `open` abre o pedido agora (sem isto, os do catálogo). */
    medications: z
        .object({
        items: z
            .array(z.object({ name: z.string().trim().min(1).max(80), quantity: z.string().trim().max(40) }))
            .max(10),
        open: z.boolean(),
    })
        .optional(),
});
export const updateTreatmentSchema = z
    .object({
    phase: z.number().int().min(0),
    nextReviewDay: isoDate.nullable(),
    reviewEveryDays: z.number().int().min(1).max(365).nullable(),
    professionalId: z.uuid().nullable(),
    priceCents: z.number().int().min(0).max(100_000_000).nullable(),
    notes: z.string().trim().max(CLINIC_NOTES_MAX),
    plannedEndDay: isoDate.nullable(),
})
    .partial();
export const TREATMENT_EVENT_KINDS = [
    'inicio',
    'fase',
    'retorno',
    'pagamento',
    'nota',
    'avaliacao',
    'consulta',
    'finalizado',
    'cancelado',
    'reaberto',
    'importado',
    'cadastro',
];
export const treatmentEventSchema = z.object({
    id: z.uuid(),
    kind: z.enum(TREATMENT_EVENT_KINDS),
    text: z.string(),
    /** Quem fez: nome do membro ou "Secretário". */
    actor: z.string(),
    at: isoDateTime,
});
// ── Agenda ──────────────────────────────────────────────────────────────────
export const CLINIC_APPOINTMENT_STATUSES = [
    'marcado',
    'confirmado',
    'compareceu',
    'faltou',
    'cancelado',
];
export const clinicAppointmentStatusSchema = z.enum(CLINIC_APPOINTMENT_STATUSES);
export const CLINIC_APPOINTMENT_STATUS_LABEL = {
    marcado: 'Sem resposta',
    confirmado: 'Confirmada',
    compareceu: 'Compareceu',
    faltou: 'Faltou',
    cancelado: 'Cancelada',
};
export const CLINIC_APPOINTMENT_SOURCES = [
    'bolso',
    'secretario',
    'pagina',
    'paciente',
    'importado',
];
/** Consulta na clínica ou por vídeo (rodada 15, telemedicina). */
export const clinicAppointmentModeSchema = z.enum(['presencial', 'online']);
export const clinicAppointmentSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid().nullable(),
    patientName: z.string(),
    professionalId: z.uuid().nullable(),
    catalogId: z.uuid().nullable(),
    /** Nome do tipo (Retorno, Bioimpedância) na hora de marcar. */
    label: z.string(),
    startAt: isoDateTime,
    durationMin: z.number().int().min(5).max(480),
    status: clinicAppointmentStatusSchema,
    source: z.enum(CLINIC_APPOINTMENT_SOURCES),
    notes: z.string().max(500),
    /** Confirmação ou lembrete já enviado pelo Sec a quem tem o app. */
    reminderSentAt: isoDateTime.nullable(),
    /** Link de confirmar sem conta, para o lembrete pelo WhatsApp (consultas futuras). */
    confirmUrl: z.string().nullable().optional(),
    /** Quantas vezes mudou de horário (rodada 15); a consulta continua uma só. */
    reschedules: z.number().int().min(0).optional(),
    /**
     * Cobrança da consulta (rodada 15), quando o tipo tem preço. O valor só vai para quem
     * vê o financeiro; os outros veem só se está paga.
     */
    /** Presencial ou online (rodada 15); online leva o link da sala. */
    mode: clinicAppointmentModeSchema.optional(),
    videoUrl: z.string().nullable().optional(),
    /** Quando foi desmarcada e por quem (a regra de faltas olha a antecedência). */
    cancelledAt: isoDateTime.nullable().optional(),
    cancelledBy: z.enum(['clinica', 'aluno']).nullable().optional(),
    /** Falta ou desmarcada em cima que o profissional abonou: não sai do saldo. */
    sessionCharge: z.enum(['abonada']).nullable().optional(),
    /** Reposição: a sessão desmarcada a tempo que esta repõe. */
    makeupOf: z.uuid().nullable().optional(),
    charge: z
        .object({
        transactionId: z.uuid(),
        amountCents: z.number().int().nullable(),
        paid: z.boolean(),
        method: z.string().nullable(),
        dueDay: isoDate,
    })
        .nullable()
        .optional(),
});
export const createClinicAppointmentSchema = z.object({
    patientId: z.uuid().optional(),
    /** Para marcar alguém que ainda não é paciente (vira lead). */
    patientName: z.string().trim().min(1).max(80).optional(),
    patientPhone: z.string().trim().max(32).optional(),
    professionalId: z.uuid().optional(),
    catalogId: z.uuid().optional(),
    label: z.string().trim().max(60).optional(),
    startAt: isoDateTime,
    durationMin: z.number().int().min(5).max(480).optional(),
    notes: z.string().trim().max(500).optional(),
    mode: clinicAppointmentModeSchema.optional(),
    /** Sala da consulta online; sem ela, vai o link padrão da clínica. */
    videoUrl: z.url().max(300).optional(),
    /** Marca como reposição da sessão desmarcada a tempo (mensalidade do estúdio). */
    makeupOf: z.uuid().optional(),
});
export const updateClinicAppointmentSchema = z
    .object({
    status: clinicAppointmentStatusSchema,
    startAt: isoDateTime,
    durationMin: z.number().int().min(5).max(480),
    professionalId: z.uuid().nullable(),
    notes: z.string().trim().max(500),
    mode: clinicAppointmentModeSchema,
    videoUrl: z.url().max(300).nullable(),
    sessionCharge: z.enum(['abonada']).nullable(),
})
    .partial();
export const scheduleBlockSchema = z.object({
    id: z.uuid(),
    professionalId: z.uuid().nullable(),
    startAt: isoDateTime,
    endAt: isoDateTime,
    reason: z.string().max(80),
    /** Repete toda semana no mesmo dia e horário. */
    weekly: z.boolean(),
});
export const createScheduleBlockSchema = scheduleBlockSchema
    .omit({ id: true })
    .partial({ professionalId: true, reason: true, weekly: true });
export const agendaDaySchema = z.object({
    day: isoDate,
    appointments: z.array(clinicAppointmentSchema),
    blocks: z.array(scheduleBlockSchema),
});
// ── Financeiro ──────────────────────────────────────────────────────────────
export const TRANSACTION_KINDS = ['entrada', 'parcela', 'avulso', 'estorno'];
export const transactionSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    patientName: z.string(),
    treatmentId: z.uuid().nullable(),
    kind: z.enum(TRANSACTION_KINDS),
    label: z.string(),
    amountCents: z.number().int(),
    dueDay: isoDate,
    paidAt: isoDateTime.nullable(),
    method: paymentMethodSchema.nullable(),
    /** Derivado: pago, previsto (a vencer) ou atrasado (vencido sem pagar). */
    status: z.enum(['pago', 'previsto', 'atrasado']),
    /** Quem paga: o paciente, ou a operadora do cartão (sem cobrança ao paciente). */
    payer: z.enum(['paciente', 'operadora']).optional(),
    /** Taxa da operadora sobre este repasse. */
    feeCents: z.number().int().min(0).optional(),
    /** Conta em que o dinheiro entrou. */
    accountId: z.uuid().nullable().optional(),
});
export const createTransactionSchema = z.object({
    patientId: z.uuid(),
    treatmentId: z.uuid().optional(),
    kind: z.enum(TRANSACTION_KINDS).optional(),
    label: z.string().trim().min(1).max(60),
    amountCents: z.number().int().min(-100_000_000).max(100_000_000),
    dueDay: isoDate,
    paidAt: isoDateTime.optional(),
    method: paymentMethodSchema.optional(),
});
export const settleTransactionSchema = z.object({
    paidAt: isoDateTime.optional(),
    method: paymentMethodSchema.optional(),
    /** Em qual conta o dinheiro entrou (banco, caixa, operadora). */
    accountId: z.uuid().optional(),
});
// ── Avaliações ──────────────────────────────────────────────────────────────
export const ASSESSMENT_TYPES = [
    'bioimpedancia',
    'antropometria',
    'fotos',
    'questionario',
    'odontograma',
];
export const assessmentTypeSchema = z.enum(ASSESSMENT_TYPES);
/** Bioimpedância, versão 1: só números; o app desenha o corpo e as barras. */
export const bioimpedanceDataSchema = z.object({
    weightKg: z.number().min(10).max(400).optional(),
    heightCm: z.number().min(80).max(250).optional(),
    fatPercent: z.number().min(1).max(70).optional(),
    muscleKg: z.number().min(5).max(120).optional(),
    waterPercent: z.number().min(20).max(80).optional(),
    visceralLevel: z.number().min(1).max(30).optional(),
    waistCm: z.number().min(30).max(250).optional(),
    hipCm: z.number().min(30).max(250).optional(),
    bmr: z.number().min(500).max(5000).optional(),
});
export const assessmentSchema = z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    professionalId: z.uuid().nullable(),
    type: assessmentTypeSchema,
    schemaVersion: z.number().int().min(1),
    data: z.record(z.string(), z.unknown()),
    measuredAt: isoDateTime,
    visibleToPatient: z.boolean(),
});
export const createAssessmentSchema = z.object({
    type: assessmentTypeSchema,
    data: z.record(z.string(), z.unknown()),
    measuredAt: isoDateTime.optional(),
    visibleToPatient: z.boolean().optional(),
});
/** Bioimpedância pelo link de compartilhar do Galileu (b.IA). */
export const galileuImportSchema = z.object({
    link: z.string().trim().min(10).max(600),
});
// ── Ficha, Hoje e Números ───────────────────────────────────────────────────
export const patientDetailSchema = z.object({
    patient: patientSchema,
    treatments: z.array(treatmentSchema),
    appointments: z.array(clinicAppointmentSchema),
    transactions: z.array(transactionSchema),
    assessments: z.array(assessmentSchema),
    events: z.array(treatmentEventSchema),
    /** O que aconteceu nos funis (rodada 15), para a linha do tempo única; em campo à parte para os apps antigos. */
    funnelEvents: z
        .array(z.object({
        id: z.uuid(),
        text: z.string(),
        actor: z.string(),
        at: isoDateTime,
        funnel: z.string(),
    }))
        .optional(),
    /** Marketing (rodada 15); em campo à parte para os apps antigos. */
    marketing: patientMarketingSchema.optional(),
    /** Saldo do plano ativo (pacote ou mensalidade), pela regra de faltas da clínica. */
    balance: sessionBalanceSchema.nullable().optional(),
    /** Programa conjunto em andamento (P3): o saldo de cada parte. */
    program: programProgressSchema.nullable().optional(),
});
export const todayPendingSchema = z.object({
    kind: z.enum(['confirmar', 'cobrar', 'retorno']),
    patientId: z.uuid(),
    patientName: z.string(),
    text: z.string(),
    appointmentId: z.uuid().nullable(),
    transactionId: z.uuid().nullable(),
    treatmentId: z.uuid().nullable(),
});
/** Uma consulta de amanhã na lista "Lembretes de amanhã" do Hoje. */
export const clinicReminderRowSchema = z.object({
    appointmentId: z.uuid(),
    patientId: z.uuid().nullable(),
    patientName: z.string(),
    /** Telefone como a clínica escreveu, para o botão do WhatsApp. */
    phone: z.string().nullable(),
    startAt: isoDateTime,
    label: z.string(),
    status: clinicAppointmentStatusSchema,
    /** `sec`: o paciente tem o app ligado e o aviso sai sozinho às 18h; `whatsapp`: botão. */
    channel: z.enum(['sec', 'whatsapp']),
    sentAt: isoDateTime.nullable(),
    /** Link de confirmar sem conta, que vai no lembrete pelo WhatsApp (G1). */
    confirmUrl: z.string().nullable().optional(),
});
/**
 * Texto do lembrete que a clínica manda pelo WhatsApp (o mesmo no app, na mesa e no Secretário).
 * Com o link de confirmar, a pessoa responde num toque; sem ele, responde na conversa.
 */
export function clinicReminderText(input) {
    const first = input.patientName.trim().split(/\s+/)[0] ?? '';
    const online = input.online && input.videoUrl ? ` É online, neste link: ${input.videoUrl}` : '';
    const ask = input.confirmUrl
        ? ` Para confirmar ou remarcar, é só tocar: ${input.confirmUrl}`
        : ' Posso confirmar?';
    return `Olá, ${first}! Passando para lembrar da sua ${input.label.toLowerCase()} ${input.when}.${online}${ask}`;
}
// ── Confirmar pelo link do lembrete (G1): página sem conta ──────────────────
export const CLINIC_CONFIRM_ACTIONS = ['confirmar', 'remarcar', 'nao_vou'];
export const clinicConfirmActionSchema = z.object({ action: z.enum(CLINIC_CONFIRM_ACTIONS) });
/** O que a página de confirmar mostra: só o mínimo (sem sobrenome, sem valor, sem saúde). */
export const clinicConfirmViewSchema = z.object({
    clinicId: z.uuid(),
    clinicName: z.string(),
    /** Versão do logotipo redondo, quando a clínica tem. */
    logoVersion: z.number().int().nullable(),
    slug: z.string().nullable(),
    patientFirstName: z.string(),
    label: z.string(),
    startAt: isoDateTime,
    timezone: z.string(),
    address: z.string().nullable(),
    online: z.boolean(),
    /** A resposta que a pessoa já deu por este link, se deu. */
    answer: z.enum(CLINIC_CONFIRM_ACTIONS).nullable(),
    /** A consulta já não está de pé (desmarcada, aconteceu). */
    closed: z.boolean(),
    theme: z.object({ light: z.string().nullable().optional() }).nullable(),
    /** Mostra "Quer o lembrete no celular?" (a clínica pode desligar). */
    invite: z.boolean().default(false),
});
export const clinicTodaySchema = z.object({
    day: isoDate,
    appointmentsToday: z.number().int().min(0),
    missedYesterday: z.number().int().min(0),
    receivedWeekCents: z.number().int().min(0),
    overdueReviews: z.number().int().min(0),
    pending: z.array(todayPendingSchema),
    agenda: z.array(clinicAppointmentSchema),
    /** O próximo dia com consulta, quando hoje está vazio. */
    nextDay: isoDate.nullable(),
    /** Consultas de amanhã, com o canal do lembrete. */
    reminders: z.array(clinicReminderRowSchema),
});
export const clinicNumbersSchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    attended: z.number().int().min(0),
    attendedBefore: z.number().int().min(0),
    missedPercent: z.number().min(0).max(100),
    missedPercentBefore: z.number().min(0).max(100),
    receivedCents: z.number().int().min(0),
    receivedBeforeCents: z.number().int().min(0),
    overdueCents: z.number().int().min(0),
    byWeek: z.array(z.object({ label: z.string(), attended: z.number().int().min(0) })),
    byHour: z.array(z.object({ label: z.string(), occupancy: z.number().min(0).max(100) })),
    origins: z.array(z.object({ label: z.string(), count: z.number().int().min(0) })),
    attendance: z.object({
        compareceu: z.number().int().min(0),
        faltou: z.number().int().min(0),
        cancelado: z.number().int().min(0),
    }),
});
// ── Mesa: Início (estudo da mesa, aprovado em 22/10) ─────────────────────
/** Paciente em tratamento ativo sem consulta marcada daqui para a frente. */
export const clinicDeskReviewSchema = z.object({
    patientId: z.uuid(),
    patientName: z.string(),
    phone: z.string().nullable(),
    treatmentId: z.uuid(),
    treatmentName: z.string(),
    /** Última vez que compareceu; null se nunca veio depois de começar. */
    lastVisitAt: isoDateTime.nullable(),
});
export const clinicDeskDaySchema = z.object({
    day: isoDate,
    /** Consultas do dia, sem as canceladas. */
    count: z.number().int().min(0),
    /** Meias horas livres no horário da clínica; null em dia que já passou ou fechado. */
    free: z.number().int().min(0).nullable(),
    closed: z.boolean(),
    /** Bloqueio que toma o dia inteiro ou só parte dele. */
    blocked: z.enum(['dia', 'parte']).nullable(),
    /** Para a dica do calendário (rodada 15): situação das consultas do dia. */
    confirmed: z.number().int().min(0).optional(),
    waiting: z.number().int().min(0).optional(),
    attended: z.number().int().min(0).optional(),
    missed: z.number().int().min(0).optional(),
    /** Consultas do dia que já mudaram de horário (contadas uma vez só). */
    rescheduled: z.number().int().min(0).optional(),
});
/** Agenda do mês na mesa: o mesmo dia a dia da Início, para qualquer mês. */
export const clinicAgendaMonthSchema = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    total: z.number().int().min(0),
    days: z.array(clinicDeskDaySchema),
    /** O mês anterior para comparar: no mês corrente, só até o mesmo dia. */
    previous: z
        .object({ total: z.number().int().min(0), untilDay: z.number().int().nullable() })
        .optional(),
});
export const clinicDeskRecentSchema = z.object({
    appointment: clinicAppointmentSchema,
    createdAt: isoDateTime,
    /** Quem marcou: o nome do membro, "Secretário", "Página da clínica" ou "Pelo app". */
    by: z.string(),
});
export const clinicDeskBirthdaySchema = z.object({
    patientId: z.uuid(),
    name: z.string(),
    age: z.number().int().min(0).nullable(),
    phone: z.string().nullable(),
});
/**
 * A Início da mesa. As quatro contas seguem as do APOLO (`server/routes.ts`,
 * `/api/dashboard/metrics`), com duas correções: "para confirmar" separa o total
 * do período das que ainda estão sem resposta, e a meta da semana é da clínica.
 */
export const clinicDeskSchema = z.object({
    day: isoDate,
    /** Hoje e o próximo dia útil (na sexta, sábado e segunda; no sábado, segunda). */
    confirmDays: z.array(isoDate).min(1).max(3),
    confirm: z.object({
        /** Consultas não canceladas nos dias da conta (o número do APOLO). */
        total: z.number().int().min(0),
        /** As que ainda não responderam e não passaram. */
        rows: z.array(clinicReminderRowSchema),
    }),
    reviews: z.object({
        total: z.number().int().min(0),
        rows: z.array(clinicDeskReviewSchema),
    }),
    goal: z.object({
        target: z.number().int().min(1),
        /** Consultas marcadas desde segunda (pela data em que foram marcadas). */
        achieved: z.number().int().min(0),
        remaining: z.number().int().min(0),
    }),
    /** Pedidos de medicamento em aberto (null com os pedidos desligados). */
    medications: z.object({ open: z.number().int().min(0) }).nullable(),
    /** Consultas da próxima semana, de segunda a domingo. */
    nextWeek: z.number().int().min(0),
    month: z.object({
        month: z.string().regex(/^\d{4}-\d{2}$/),
        total: z.number().int().min(0),
        days: z.array(clinicDeskDaySchema),
    }),
    today: z.array(clinicAppointmentSchema),
    birthdays: z.array(clinicDeskBirthdaySchema),
    recent: z.array(clinicDeskRecentSchema),
});
/** O app declara que sabe mostrar as telas da clínica (só nos pacotes novos). */
export const ASSISTANT_CAP_CLINIC = 'clinic';
// ── Página pública (estudo E12, aprovado 21/10) ────────────────────────────
export const clinicPublicServiceSchema = z.object({
    id: z.uuid(),
    kind: catalogKindSchema,
    name: z.string(),
    priceCents: z.number().int().min(0).nullable(),
    durationMin: z.number().int().min(5).max(480).nullable(),
    bookingNote: z.string().nullable(),
});
/** O cartão de `app.secretarios.net/c/<slug>`: só o que a clínica escolheu mostrar. */
export const clinicPublicCardSchema = z.object({
    clinicId: z.uuid(),
    slug: z.string(),
    name: z.string(),
    kind: clinicKindSchema,
    kindLabel: z.string(),
    description: z.string().nullable(),
    registration: z.string().nullable(),
    hours: z.array(businessHoursSchema),
    address: z.string().nullable(),
    /** Itens do catálogo ativos; preço só quando a clínica mostra e a profissão permite. */
    services: z.array(clinicPublicServiceSchema),
    secretary: z.object({ name: z.string(), characterKey: z.string() }),
    /** @usuário do dono, para o botão Falar abrir a conversa com o Secretário dele. */
    ownerUsername: z.string().nullable(),
    allowBooking: z.boolean(),
    allowFollow: z.boolean(),
    /** Tema da clínica (T7.25), só o lado claro e a letra: a página pública é sempre clara. */
    theme: z
        .object({
        light: z.string(),
        font: z.enum(CLINIC_THEME_FONTS),
        logoLight: clinicThemeBaseSchema.nullable(),
    })
        .nullable()
        .optional(),
    /** Logotipo: a versão (para o endereço da imagem) e quais existem. */
    brand: z
        .object({ version: z.number().int(), claro: z.boolean(), redondo: z.boolean() })
        .nullable()
        .optional(),
});
export const clinicPublicSlotSchema = z.object({ day: isoDate, time: z.string() });
/** Pedido de horário pela página: vira lead + consulta "sem resposta" até a clínica confirmar. */
export const clinicPublicBookingSchema = z.object({
    catalogId: z.uuid().nullable(),
    day: isoDate,
    time: z.string().regex(/^\d{2}:\d{2}$/),
    /** Link de campanha (utm_source e utm_campaign): a origem e a campanha de quem é novo. */
    source: z.string().trim().max(60).optional(),
    campaign: z.string().trim().max(80).optional(),
});
export const clinicPublicBookingResultSchema = z.object({
    appointmentId: z.uuid(),
    clinicId: z.uuid(),
    label: z.string(),
    startAt: isoDateTime,
    bookingNote: z.string().nullable(),
});
// ── Origens (de onde o paciente veio) ───────────────────────────────────────
/**
 * Origens padrão, as mesmas do CRM APOLO (FORMAN), com sigla para os relatórios e os jeitos
 * de escrever que já aparecem nas planilhas. A clínica pode desligar qualquer uma e criar as
 * suas. "Página da clínica" é a do Sec (quem marca pela página pública).
 */
export const CLINIC_DEFAULT_ORIGINS = [
    { key: 'BASE', label: 'Base', abbrev: 'BS', aliases: ['base', 'base de contatos', 'contatos'] },
    {
        key: 'LEAD',
        label: 'Lead',
        abbrev: 'LD',
        aliases: [
            'lead',
            'leads',
            'trafego pago',
            'anuncio',
            'ads',
            'facebook ads',
            'google ads',
            'meta ads',
        ],
    },
    {
        key: 'INDICACAO',
        label: 'Indicação',
        abbrev: 'IN',
        aliases: ['indicacao', 'indicado', 'indicada', 'indicou', 'amigo', 'amiga'],
    },
    {
        key: 'REDES_SOCIAIS',
        label: 'Redes sociais',
        abbrev: 'RS',
        aliases: [
            'redes sociais',
            'rede social',
            'instagram',
            'insta',
            'facebook',
            'tiktok',
            'youtube',
        ],
    },
    { key: 'PERMUTA', label: 'Permuta', abbrev: 'PM', aliases: ['permuta', 'parceria', 'parceiro'] },
    {
        key: 'PROSPECCAO_ATIVA',
        label: 'Prospecção ativa',
        abbrev: 'PA',
        aliases: ['prospeccao ativa', 'prospeccao', 'ativa', 'ligacao'],
    },
    { key: 'SITE', label: 'Site', abbrev: 'ST', aliases: ['site', 'google', 'pesquisa', 'busca'] },
    {
        key: 'INDICACAO_APP',
        label: 'Indicação pelo app',
        abbrev: 'IA',
        aliases: ['indicacao app', 'indique e ganhe', 'app'],
    },
    {
        key: 'PAGINA',
        label: 'Página da clínica',
        abbrev: 'PG',
        aliases: ['pagina', 'pagina da clinica', 'pagina publica', 'agendamento online'],
    },
];
export const CLINIC_ORIGIN_LABEL_MAX = 40;
export const clinicOriginSchema = z.object({
    id: z.uuid(),
    key: z.string(),
    label: z.string(),
    abbrev: z.string(),
    active: z.boolean(),
    /** Criada pela clínica (as padrão só se desligam). */
    custom: z.boolean(),
});
/** Quantos pacientes há em cada origem, inclusive as que não estão na lista. */
export const clinicOriginUsageSchema = z.object({
    label: z.string(),
    patients: z.number().int().min(0),
    /** Está na lista da clínica (casou pelo nome ou por sinônimo). */
    listed: z.boolean(),
});
/**
 * Contato de quem trabalha na clínica que também é paciente dela (fase E, decisão 3). A
 * ligação é lida da clínica na hora, sem cópia: pelo DDD + os 8 últimos dígitos do
 * telefone, a mesma chave do APOLO (casa com e sem o 9).
 */
export const clinicContactLinkSchema = z.object({
    contactId: z.uuid(),
    contactName: z.string(),
    patientId: z.uuid(),
    patientName: z.string(),
    status: patientStatusSchema,
    clinicName: z.string(),
});
export const createClinicOriginSchema = z.object({
    label: z.string().trim().min(2).max(CLINIC_ORIGIN_LABEL_MAX),
    abbrev: z.string().trim().min(1).max(4).optional(),
});
export const updateClinicOriginSchema = z
    .object({
    label: z.string().trim().min(2).max(CLINIC_ORIGIN_LABEL_MAX),
    abbrev: z.string().trim().min(1).max(4),
    active: z.boolean(),
})
    .partial();
/** Texto comparável: sem acento, sem espaço, hífen ou sublinhado, em minúsculas. */
export function originText(raw) {
    return raw
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[\s_\-./]+/g, '');
}
/**
 * Acha a origem de um texto livre ("instagram", "INDICAÇÃO", "Tráfego pago") entre as da
 * clínica, pela chave, pelo nome ou pelos jeitos de escrever. Devolve o nome certo ou null.
 */
export function resolveOrigin(raw, origins) {
    if (!raw || !raw.trim())
        return null;
    const wanted = originText(raw);
    for (const origin of origins) {
        if (originText(origin.key) === wanted || originText(origin.label) === wanted)
            return origin.label;
        const known = CLINIC_DEFAULT_ORIGINS.find((item) => item.key === origin.key);
        if (known?.aliases.some((alias) => originText(alias) === wanted))
            return origin.label;
    }
    return null;
}
const words = (raw) => raw
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
/**
 * A origem dentro de uma frase da conversa ("Vi no Instagram de vocês", "uma amiga me
 * indicou"): o nome ou um jeito de escrever que aparece inteiro na frase. Vence o mais
 * longo; sem nenhum, null.
 */
export function findOriginInText(raw, origins) {
    const exact = resolveOrigin(raw, origins);
    if (exact || !raw)
        return exact;
    const said = ` ${words(raw)} `;
    let best = null;
    for (const origin of origins) {
        const known = CLINIC_DEFAULT_ORIGINS.find((item) => item.key === origin.key);
        for (const name of [origin.label, ...(known?.aliases ?? [])]) {
            const phrase = words(name);
            if (phrase.length >= 4 && said.includes(` ${phrase} `) && phrase.length > (best?.size ?? 0))
                best = { label: origin.label, size: phrase.length };
        }
    }
    return best?.label ?? null;
}
// ── Equipe no mesmo prontuário (P3 do personal) ─────────────────────────────
/**
 * Papéis travados (estudo do personal, P3): a nutricionista não edita treino e o personal não
 * edita dieta. Cada um vê a área do outro só em resumo e só com o consentimento do paciente
 * (o personal, o resumo do diário alimentar; a nutricionista, carga e frequência do treino).
 * Médico, quem não marcou profissão e as outras áreas seguem como antes.
 */
export const TEAM_AREAS = ['treino', 'dieta'];
export function teamAreaAccess(profession, area) {
    if (area === 'treino' && profession === 'nutricao')
        return 'resumo';
    if (area === 'dieta' && profession === 'educacao_fisica')
        return 'resumo';
    return 'edita';
}
/** A chave do consentimento do paciente para a área ser vista em resumo pela outra profissão. */
export const TEAM_SHARE_FLAG = {
    treino: 'equipe_treino',
    dieta: 'equipe_dieta',
};
